import { useState, useEffect, useCallback } from 'react';
import { cohortsApi } from '../services/api/cohorts';
import type { CohortEmailSettings, EmailPersona } from '../types';

interface UseEmailToggleReturn {
  /** Check if a specific email type is enabled (respects 3-level cascade) */
  isEnabled: (persona: EmailPersona, emailType: string) => boolean;
  /** Whether the settings are still loading */
  loading: boolean;
  /** The full email settings object */
  settings: CohortEmailSettings | null;
}

/**
 * Hook to check email toggle state for a cohort.
 * Fetches email settings and provides cascade-aware checks:
 * master_enabled → persona.enabled → persona.emails[emailType]
 */
export function useEmailToggle(cohortId: string | undefined): UseEmailToggleReturn {
  const [settings, setSettings] = useState<CohortEmailSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cohortId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    cohortsApi.getEmailSettings(cohortId).then((data) => {
      if (!cancelled) {
        setSettings(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setSettings(null);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [cohortId]);

  const isEnabled = useCallback(
    (persona: EmailPersona, emailType: string): boolean => {
      if (!settings) return true; // Default enabled if settings unavailable
      if (!settings.master_enabled) return false;
      const personaSettings = settings.personas[persona];
      if (!personaSettings?.enabled) return false;
      return personaSettings.emails[emailType as keyof typeof personaSettings.emails] !== false;
    },
    [settings],
  );

  return { isEnabled, loading, settings };
}
